// src/App.js
import { useEffect, useState } from "react";
import { Box, ThemeProvider, Typography, Grid } from "@mui/material";
import theme from "./styles/themes/theme";
import { DataProvider, useData } from "./hooks/useData";
import AppBar from "./components/AppBar";
import SideToolbar from "./components/SideToolbar";
import ContextualDrawer from "./components/ContextualDrawer";
import Authentication from "./components/Authentication";
import CanvasArea from "./components/CanvasArea";
import BottomBar from "./components/BottomBar";
import { memo, Component } from "react";
import HotKeys from "./components/HotKeys";

class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      console.error("ErrorBoundary caught:", this.state.error);
      return (
        <Typography color="error">
          Something went wrong. Please refresh.
        </Typography>
      );
    }
    return this.props.children;
  }
}

const MainContent = memo(() => {
  const { canvas } = useData();

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#ddd" }}>
      <AppBar />
      <HotKeys />
      <Box
        sx={{
          position: "fixed",
          top: { xs: 96, sm: 113 }, // AppBar height: 96px (xs), 113px (sm)
          width: "100%",
          height: 8, // Grey margin height
          bgcolor: "grey.300",
          zIndex: 1199, // Below AppBar (1200)
        }}
      />
      <Grid
        container
        sx={{
          height: "calc(100vh - 170px)",
          mx: "auto",
          mt: { xs: 13, sm: 15.125 }, // AppBar height (96px/113px) + grey margin (8px) = 104px/121px
          flexWrap: "nowrap",
          gap: "4px",
          boxSizing: "border-box",
          overflow: "visible",
        }}
      >
        <Grid
          item
          xs={12}
          sm={2}
          md={1.5}
          lg={1.5}
          sx={{ minWidth: { xs: "100%", sm: "100px" }, margin: 0 }}
        >
          <SideToolbar />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
          md={3}
          lg={3}
          sx={{ minWidth: { xs: "100%", sm: "390px" }, margin: 0 }}
        >
          <ContextualDrawer canvas={canvas} />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={7.5}
          lg={7.5}
          sx={{
            width: "100%",
            maxWidth: "100%",
            height: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "visible",
            margin: "auto",
            boxSizing: "border-box",
          }}
        >
          <CanvasArea />
        </Grid>
        <BottomBar
          canvas={canvas}
          projectName="MyDesign"
        />
      </Grid>
    </Box>
  );
});

const App = () => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    console.log("App: Client-side rendering enabled");
  }, []);

  if (!isClient) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <ThemeProvider theme={theme}>
      <DataProvider>
        <Authentication />
        <ErrorBoundary>
          <MainContent />
        </ErrorBoundary>
      </DataProvider>
    </ThemeProvider>
  );
};

export default App;
